import Typography from '@mui/material/Typography'
import { Box, Grid, Skeleton } from '@mui/material'
import { useOrderStore } from 'modules/orders/store'
import {
  formatCurrency,
  unwrapCalculateTotalResponse,
  useDebounce
} from 'utils'
import { useLoadOrderCalculateTotal } from 'modules/orders/hooks'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { get } from 'lodash'
import { useRecalculeTaxes } from '../recalculeAlert'
import { LEFT_SIDE_WIDTH } from './Form'

type ItemProps = {
  isCalculating: boolean
  label: string
  value: number
}

const totals = [
  { label: 'Total Item', value: 'total.totalItems' },
  { label: 'Acrésc. Vlr Item', value: 'total.totalAdditional' },
  { label: 'Total IPI', value: 'total.totalIpi' },
  { label: 'Total ICMS', value: 'total.totalIcms' },
  { label: 'Total ST', value: 'total.totalSt' },
  { label: 'Frete', value: 'total.freightPrice' },
  { label: 'Desconto', value: 'total.totalDiscount' },
  { label: 'Despesas', value: 'total.totalExpenses' }
]

const Item = (props: ItemProps) => {
  const { isCalculating, label, value } = props
  return (
    <>
      <Grid item xs={6}>
        <Typography variant="subtitle2">{label}</Typography>
      </Grid>
      <Grid item xs={6} container justifyContent="flex-end">
        {!isCalculating && (
          <Typography variant="subtitle2">{formatCurrency(value)}</Typography>
        )}
        {isCalculating && <Skeleton width={80} animation="wave" height={22} />}
      </Grid>
    </>
  )
}

export default function Total() {
  const form = useOrderStore.useForm()
  const setForm = useOrderStore.useSetForm()
  const isTaxesRecalculePending = useOrderStore.useIsTaxesRecalculePending()
  const isRecalculatingDuplicateOrder =
    useOrderStore.useIsRecalculatingDuplicateOrder()
  const { watch } = useFormContext()

  const discountDeb = useDebounce(watch('totalDiscount'), 500)
  const expensesDeb = useDebounce(watch('totalExpenses'), 500)
  const freightPriceDeb = useDebounce(watch('freightPrice'), 500)

  const { data, isLoading, isFetching } = useLoadOrderCalculateTotal({
    isTaxesRecalculePending,
    totalDiscount: Number(discountDeb || 0),
    totalExpenses: Number(expensesDeb || 0),
    freightPrice: Number(freightPriceDeb || 0),
    items: form?.orderItems
  })

  useRecalculeTaxes({
    formData: { ...watch(), items: form?.orderItems }
  })

  const isCalculating = isLoading || isFetching || isRecalculatingDuplicateOrder

  useEffect(() => {
    if (!data || isRecalculatingDuplicateOrder) return

    const total = unwrapCalculateTotalResponse(data)
    if (total) setForm({ total })
  }, [data, isRecalculatingDuplicateOrder, setForm])

  return (
    <Box
      sx={{
        width: LEFT_SIDE_WIDTH - 16,
        padding: 1,
        flexShrink: 0
      }}
    >
      <Grid container justifyContent="space-between" alignItems="center">
        {totals.map((item) => (
          <Item
            key={item.label}
            label={item.label}
            value={get(form, item.value)}
            isCalculating={isCalculating}
          />
        ))}

        <Grid item xs={6}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 'bold', color: '#389e0d' }}
          >
            Total:
          </Typography>
        </Grid>
        <Grid item xs={6} container justifyContent="flex-end">
          {!isCalculating && (
            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold', color: '#389e0d' }}
            >
              {formatCurrency(form?.total?.total || 0)}
            </Typography>
          )}
          {isCalculating && (
            <Skeleton width={110} animation="wave" height={28} />
          )}
        </Grid>
      </Grid>
    </Box>
  )
}
